import { computed, inject, Injectable, linkedSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, EventType, IsActiveMatchOptions, Router } from '@angular/router';
import { UnityRouteUrlTrees } from '@unity/utils/types';

@Injectable({
  providedIn: 'root',
})
export class RouteService {
  private readonly router = inject(Router);

  private readonly urlCompareOptions = (paths: 'subset' | 'exact') =>
    ({
      paths: paths,
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    }) as IsActiveMatchOptions;

  public getActiveRoute(): ActivatedRoute {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  public getActiveUnityUrl(): UnityRouteUrlTrees {
    return this.getActiveRoute().snapshot.url.map(x => x.path) as UnityRouteUrlTrees;
  }

  public isActive(link: UnityRouteUrlTrees, match: 'exact' | 'subset') {
    const url = this.router.createUrlTree(link);
    const options = this.urlCompareOptions(match);
    return this.router.isActive(url, options);
  }

  /** Signals Implementation */

  private readonly $routerEvent = toSignal(this.router.events);
  private readonly $activeRoute = computed(() => {
    const event = this.$routerEvent();
    const isNav = event && event.type === EventType.NavigationEnd;
    if (isNav) {
      return this.getActiveRoute();
    }
    return undefined;
  });

  private readonly $routeChangedValue = linkedSignal<ActivatedRoute | undefined, ActivatedRoute | undefined>({
    source: this.$activeRoute,
    computation: (activeRoute, previous) => (!!activeRoute && activeRoute !== previous?.value ? activeRoute : previous?.value),
  });

  public readonly $routeChange = this.$routeChangedValue.asReadonly();
}
